import styles from './PlaylistStats.module.css'

import cn from 'clsx'
import lodashSumBy from 'lodash/sumBy'
import React, { useCallback, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { IS_ARTIST } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import type {
  Playlist,
  PlaylistStatsByUser,
  PlaylistTrackStats,
} from 'types/playlist'

import PricingBadge from 'components/ui/pricing-badge'

import { LuArrowDown } from '@react-icons/all-files/lu/LuArrowDown'
import { LuArrowUp } from '@react-icons/all-files/lu/LuArrowUp'
import { LuArrowUpDown } from '@react-icons/all-files/lu/LuArrowUpDown'
import { LuInfo } from '@react-icons/all-files/lu/LuInfo'
import { LuX } from '@react-icons/all-files/lu/LuX'
import {
  Avatar,
  Box,
  Dialog,
  DiscoCallout,
  Flex,
  Grid,
  IconButton,
  Link,
  Popover,
  Skeleton,
  Table,
  Text,
  Tooltip,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'

type TAggregatedEventsEnum = 'track_downloads' | 'track_streams'

type TSortColumn =
  | 'recipient'
  | 'views'
  | 'playlist_downloads'
  | 'track_downloads'
  | 'streams'
type TSortDirection = 'asc' | 'desc'

type Props = {
  isFeatureEnabled?: boolean
  isTrial: boolean
  playlist: Playlist
  onClose?: () => void
}

const getUsername = (user: PlaylistStatsByUser['user']) =>
  user.id !== null
    ? user.name || user.email || 'Unnamed share'
    : 'Other recipients'

const getAggregatedValue = (
  { stats, track_stats }: PlaylistStatsByUser,
  aggregatedEventType: TAggregatedEventsEnum,
) =>
  track_stats[aggregatedEventType].length === 0
    ? stats[aggregatedEventType]
    : lodashSumBy(track_stats[aggregatedEventType], 'count')

const getSortValue = (row: PlaylistStatsByUser, column: TSortColumn) => {
  switch (column) {
    case 'recipient':
      return getUsername(row.user).toLowerCase()
    case 'views':
      return row.stats.playlist_accesses
    case 'playlist_downloads':
      return row.stats.playlist_downloads
    case 'track_downloads':
      return getAggregatedValue(row, 'track_downloads')
    case 'streams':
      return getAggregatedValue(row, 'track_streams')
  }
}

const compareSortValues = (a: string | number, b: string | number) =>
  typeof a === 'string' && typeof b === 'string'
    ? a.localeCompare(b)
    : (a as number) - (b as number)

const getRowKey = (row: PlaylistStatsByUser) =>
  row.user.id ?? 'other-recipients'

const PlaylistStats = ({
  isFeatureEnabled,
  isTrial,
  playlist,
  onClose,
}: Props) => {
  const dispatch = useDispatch()
  const isArtist = useFeatureEnabled(IS_ARTIST)
  const isPhone = useSelector(isDeviceTypePhone)

  const [sortColumn, setSortColumn] = useState<TSortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<TSortDirection>('asc')

  const handleSort = useCallback(
    (column: TSortColumn) => {
      if (sortColumn === column) {
        setSortDirection(direction => (direction === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortColumn(column)
        setSortDirection('asc')
      }
    },
    [sortColumn],
  )

  const isEmpty: boolean = useMemo(() => {
    if (!playlist) return true
    const { stats_all, stats_by_user } = playlist
    if (stats_all && stats_by_user) {
      return (
        stats_by_user.length +
          stats_all.playlist_accesses +
          stats_all.playlist_downloads +
          stats_all.track_downloads +
          stats_all.track_streams ===
        0
      )
    }
    return true
  }, [playlist])

  const handleContactsLinkClick = useCallback(() => {
    dispatch(sendMixpanelEvent('Access Contacts via playlist stats CTA'))
  }, [dispatch])

  const handleUserClick = useCallback(() => {
    dispatch(sendMixpanelEvent('Access Contacts via playlist stats contact'))
  }, [dispatch])

  const renderCell = (
    { stats, track_stats }: PlaylistStatsByUser,
    label: string,
    aggregatedEventType: TAggregatedEventsEnum,
  ) => {
    const popover = !isFeatureEnabled ? (
      <div className={styles.playlistStatsPopover}>
        <Tooltip
          content={
            <>
              Upgrade to see which tracks were streamed
              <br />
              and how many times.
              <br />
              <PricingBadge title="PLUS" />
            </>
          }
        >
          <span>{stats[aggregatedEventType]}</span>
        </Tooltip>
      </div>
    ) : (
      <Popover.Root>
        <Popover.Trigger>
          <div className={styles.playlistStatsPopover}>
            <span>
              {lodashSumBy(track_stats[aggregatedEventType], 'count')}
            </span>
          </div>
        </Popover.Trigger>
        <Popover.Content>
          <Text size="2" weight="bold">
            {label}
          </Text>
          <Box mt="2">
            {track_stats[aggregatedEventType].map(
              (item: PlaylistTrackStats, index: number) => (
                <Text as="div" size="2" key={index}>
                  {`${item.track.name || 'Untitled Track'} - ${item.count}`}
                </Text>
              ),
            )}
          </Box>
        </Popover.Content>
      </Popover.Root>
    )

    return track_stats[aggregatedEventType].length === 0
      ? stats[aggregatedEventType]
      : popover
  }

  const renderUser = (user: PlaylistStatsByUser['user']) => {
    const username = getUsername(user)
    const initials = username
      .split(' ')
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase() ?? '')
      .join('')

    const avatar = (
      <Avatar
        size="2"
        radius="full"
        fallback={initials}
        className={styles.playlistStatsUserAvatar}
      />
    )
    const nameEl = (
      <Flex align="center" gap="1" minWidth="0" flexGrow="1">
        <Text size="2" truncate>
          {username}
        </Text>
        {user.id === null && (
          <Tooltip content="Recipients who accessed this playlist but are not in your contacts.">
            <span className={styles.playlistStatsInfoIcon}>
              <LuInfo size={14} />
            </span>
          </Tooltip>
        )}
      </Flex>
    )

    return user.contact_id ? (
      <RouterLink
        className={styles.playlistStatsUser}
        to={`/contacts/${user.contact_id}/activity`}
        onClick={handleUserClick}
      >
        {avatar}
        {nameEl}
      </RouterLink>
    ) : (
      <span className={styles.playlistStatsUser}>
        {avatar}
        {nameEl}
      </span>
    )
  }

  const renderSortableHeader = (
    label: string,
    column: TSortColumn,
    align: 'start' | 'end' = 'end',
  ) => {
    const isActive = sortColumn === column
    const Icon = isActive
      ? sortDirection === 'asc'
        ? LuArrowUp
        : LuArrowDown
      : LuArrowUpDown

    return (
      <Table.ColumnHeaderCell
        justify={align}
        aria-sort={
          isActive
            ? sortDirection === 'asc'
              ? 'ascending'
              : 'descending'
            : 'none'
        }
      >
        <button
          type="button"
          className={cn(
            styles.sortableHeader,
            align === 'end' && styles.sortableHeaderEnd,
            isActive && styles.sortableHeaderActive,
          )}
          onClick={() => handleSort(column)}
        >
          {label}
          <Icon size={12} className={styles.sortIcon} />
        </button>
      </Table.ColumnHeaderCell>
    )
  }

  const renderTable = (statsByUser: PlaylistStatsByUser[]) => {
    const sortedStatsByUser = sortColumn
      ? [...statsByUser].sort(
          (a, b) =>
            compareSortValues(
              getSortValue(a, sortColumn),
              getSortValue(b, sortColumn),
            ) * (sortDirection === 'asc' ? 1 : -1),
        )
      : statsByUser

    return (
      <Table.Root mt="3" className={styles.recipientTable}>
        <Table.Header>
          <Table.Row>
            {renderSortableHeader('Recipient', 'recipient', 'start')}
            {renderSortableHeader('Views', 'views')}
            {renderSortableHeader('Playlist downloads', 'playlist_downloads')}
            {renderSortableHeader('Track downloads', 'track_downloads')}
            {renderSortableHeader('Streams', 'streams')}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sortedStatsByUser.map(row => (
            <Table.Row key={getRowKey(row)}>
              <Table.Cell>{renderUser(row.user)}</Table.Cell>
              <Table.Cell justify="end">
                {row.stats.playlist_accesses}
              </Table.Cell>
              <Table.Cell justify="end">
                {row.stats.playlist_downloads}
              </Table.Cell>
              <Table.Cell justify="end">
                {renderCell(row, 'Track downloads', 'track_downloads')}
              </Table.Cell>
              <Table.Cell justify="end">
                {renderCell(row, 'Streams', 'track_streams')}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    )
  }

  const renderContents = () => {
    const { stats_all, stats_by_user } = playlist

    if (!stats_all || isEmpty) {
      return (
        <Flex direction="column" align="center" gap="3" py="9">
          <img
            srcSet={`${require('assets/images/no-activity.png')}, ${require('assets/images/no-activity@2x.png')} 2x`}
            className={styles.emptyImage}
          />
          <Text size="3" weight="bold">
            No activity here yet
          </Text>
          <Text size="2" color="gray">
            Generate some activity by{' '}
            <Link asChild>
              <RouterLink
                to={`/explorer/playlists/${playlist.playlist_id}/share`}
              >
                sharing your list.
              </RouterLink>
            </Link>
          </Text>
        </Flex>
      )
    }

    const statCards = [
      { label: 'Page views', value: stats_all.playlist_accesses },
      { label: 'Playlist downloads', value: stats_all.playlist_downloads },
      { label: 'Track downloads', value: stats_all.track_downloads },
      { label: 'Streams', value: stats_all.track_streams },
    ]

    return (
      <>
        <Grid columns={isPhone ? '2' : '4'} gap="3" mb="5">
          {statCards.map(card => (
            <Box key={card.label} className={styles.statCard}>
              <Text size="2" color="gray" className={styles.statCardLabel}>
                {card.label}
              </Text>
              <Text size="7" weight="bold" className={styles.statCardValue}>
                {card.value}
              </Text>
            </Box>
          ))}
        </Grid>

        <Text
          size="1"
          weight="bold"
          color="gray"
          className={styles.sectionLabel}
        >
          BY RECIPIENT
        </Text>

        {renderTable(stats_by_user)}
      </>
    )
  }

  const playlistName = playlist?.name || 'Untitled playlist'

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose?.()}>
      <Dialog.Content maxWidth="780px" className={styles.dialogContent}>
        <Dialog.Close className={styles.closeButton}>
          <IconButton variant="ghost" color="gray" size="2">
            <LuX size={18} />
          </IconButton>
        </Dialog.Close>
        <Text
          size="1"
          weight="bold"
          color="gray"
          className={styles.sectionLabel}
        >
          PLAYLIST STATS
        </Text>
        <Dialog.Title mt="1" mb="4">
          {playlistName}
        </Dialog.Title>

        {(!isFeatureEnabled || isTrial) && !isArtist && (
          <Box mb="4">
            <DiscoCallout type="warning">
              {isTrial ? (
                <>
                  Full playlist stats, including which tracks were played and
                  downloaded and how many times, are available on&nbsp;
                  <Link asChild>
                    <RouterLink to="/settings/billing">
                      Plus and Pro plans.
                    </RouterLink>
                  </Link>
                </>
              ) : (
                <>
                  Want more stats, like which tracks were streamed and
                  downloaded, and how many times?&nbsp;
                  <PricingBadge title="upgrade" />
                </>
              )}
            </DiscoCallout>
          </Box>
        )}

        {playlist?.has_stats_loaded ? (
          renderContents()
        ) : (
          <>
            <Grid columns={isPhone ? '2' : '4'} gap="3" mb="5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height="120px" />
              ))}
            </Grid>
            <Skeleton height="var(--font-size-1)" width="120px" mb="3" />
            <Flex direction="column" gap="3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height="40px" />
              ))}
            </Flex>
          </>
        )}

        {playlist?.has_stats_loaded && !isEmpty && (
          <Text size="2" color="gray" mt="4" className={styles.footer}>
            To manage recipients and view full activity, visit{' '}
            <Link asChild>
              <RouterLink to="/contacts/list" onClick={handleContactsLinkClick}>
                Contacts
              </RouterLink>
            </Link>
            .
          </Text>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default React.memo<Props>(PlaylistStats)
